import {LogOut, MessageCircle, Smartphone} from 'lucide-react';
import {PhoneHQDialog} from 'ch3-ui-lib';

interface LogoutDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}

const icon = '!p-0 shrink-0 !text-current';

/** Fired anywhere in the app (e.g. Settings) to open the log out confirmation owned by the sidebar. */
export const LOGOUT_REQUEST_EVENT = 'phq:logout-request';
export const requestLogout = () => window.dispatchEvent(new Event(LOGOUT_REQUEST_EVENT));

/** Settings-LogoutDialog from the design: illustrated header, two reassurance lines, full-width actions. */
export const LogoutDialog = ({open, onOpenChange, onConfirm}: LogoutDialogProps) => (
    <PhoneHQDialog
        open={open}
        onOpenChange={onOpenChange}
        close={false}
        noPadding
        contentClassName="!max-w-[min(440px,calc(100%-2rem))] !gap-0 overflow-hidden !rounded-card !border-0 !bg-surface !text-ink shadow-overlay"
    >
        <div role="alertdialog" aria-labelledby="logout-title" aria-describedby="logout-desc">
            <div aria-hidden="true" className="relative flex h-32 items-center justify-center overflow-hidden bg-brand-tint-subtle">
                <svg width="440" height="128" viewBox="0 0 440 128" className="absolute inset-0 text-brand-tint">
                    <g fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="220" cy="64" r="52"/>
                        <circle cx="220" cy="64" r="84"/>
                        <circle cx="220" cy="64" r="116"/>
                    </g>
                    <g fill="currentColor">
                        <rect x="64" y="24" width="20" height="20" rx="6"/>
                        <rect x="356" y="76" width="28" height="28" rx="8"/>
                        <rect x="104" y="92" width="12" height="12" rx="4"/>
                        <rect x="332" y="22" width="12" height="12" rx="4"/>
                    </g>
                </svg>
                <span className="relative flex size-16 items-center justify-center rounded-sheet bg-brand text-white ring-[6px] ring-brand-tint">
                    <LogOut size={28} className={icon}/>
                </span>
            </div>

            <div className="flex flex-col gap-4 p-6">
                <div className="flex flex-col gap-2 text-center">
                    <h2 id="logout-title" className="m-0 text-[20px] font-semibold leading-[1.3] text-ink">Log out of PhoneHQ?</h2>
                    <p id="logout-desc" className="m-0 text-callout text-ink-muted-strong">
                        You’ll need to sign in again to make and receive calls on this device.
                    </p>
                </div>
                <ul className="m-0 flex list-none flex-col gap-2 p-0 text-footnote text-ink-muted-strong">
                    <li className="flex items-center gap-2.5"><Smartphone size={16} aria-hidden="true" className={icon}/>Only this device is signed out</li>
                    <li className="flex items-center gap-2.5"><MessageCircle size={16} aria-hidden="true" className={icon}/>Your chats and call history stay in your account</li>
                </ul>
                <div className="mt-2 flex gap-2">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center rounded-control border border-solid border-fill-subtle bg-surface px-4 text-callout font-medium text-ink hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="logout-confirm"
                        onClick={onConfirm}
                        className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-control border-0 bg-brand px-4 text-callout font-medium text-white hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2"
                    >
                        <LogOut size={16} aria-hidden="true" className={icon}/>
                        Log out
                    </button>
                </div>
            </div>
        </div>
    </PhoneHQDialog>
);

export default LogoutDialog;
